import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { JWT } from 'next-auth/jwt';
import { classnames } from 'tailwindcss-classnames';
import { useRouter } from 'next/router';
import Modal from 'components/helpers/Modal';
import Button from 'components/helpers/Button';
import useSafeSession from 'lib/util/useSafeSession';
import fnNoOp from 'lib/util/no-op';
import { logError, logInfo, makeLoggableError } from 'lib/util/errors';
import { parseRegionClaim, ParsedRegionClaim } from 'lib/util/audiences';
import { extractViewAll } from 'lib/auth/utils';
import { env } from 'lib/env';
import useKonamiCode from 'lib/react/useKonamiCode';

const serviceUrl = '/api/user';

const removeValue = (
  setter: Dispatch<SetStateAction<Array<string>>>,
  values: Array<string>,
  index: number
) => {
  setter(values.filter((_, i) => i !== index));
};

/**
 * The `ImpersonateComponent` is a React functional component that provides a user interface
 * for impersonating a user and managing authorization roles and regions. It includes functionality
 * for loading user claims, modifying authorization roles, and applying impersonation settings.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @remarks
 * - This component uses hooks such as `useState`, `useEffect`, and `useCallback` to manage state and side effects.
 * - It interacts with APIs to fetch and update user claims and authorization data.
 * - Includes a Konami code Easter egg to open the impersonation modal.
 *
 * @example
 * ```tsx
 * <ImpersonateComponent />
 * ```
 *
 * @dependencies
 * - `useSafeSession`: Custom hook to retrieve session data.
 * - `useRouter`: Next.js router for navigation and page reloads.
 * - `Modal`: A modal component for displaying impersonation settings.
 * - `Button`: A reusable button component.
 * - Utility functions: `parseRegionClaim`, `extractViewAll`, `makeLoggableError`, `logError`, `fnNoOp`.
 *
 * @state
 * - `isAuthenticated` (`boolean`): Tracks if the user is authenticated.
 * - `isOpen` (`boolean`): Controls the visibility of the impersonation modal.
 * - `authzSite` (`string[]`): Stores the list of authorized sites.
 * - `authzSiteOptions` (`[string, string][]`): Options for site roles.
 * - `authzCenter` (`string[]`): Stores the list of authorized centers.
 * - `authzRegion` (`ParsedRegionClaim`): Stores the parsed region claims.
 * - `newValue` (`string`): Stores the value for adding new roles.
 * - `impersonationAccount` (`string`): Stores the impersonation account input.
 *
 * @methods
 * - `onRegionChanged`: Handles changes to region checkboxes.
 * - `loadTokenResponse`: Processes token claims and updates state.
 * - `addValue`: Adds a new value to a specified state array.
 * - `onLoadAccountClicked`: Loads claims for the impersonation account.
 * - `onImpersonationUserValueChanged`: Updates the impersonation account input.
 * - `onApplyRolesClicked`: Applies the updated roles and reloads the page.
 * - `closeModal`: Closes the impersonation modal.
 *
 * @effects
 * - Fetches authorization site options and user claims when authenticated.
 * - Listens for the Konami code to open the modal.
 *
 * @modals
 * - Displays a modal for managing impersonation settings, including site roles, regions, and centers.
 *
 * @errors
 * - Handles API errors and logs them using `logError`.
 * - Displays alerts for unexpected errors or invalid input.
 */
const ImpersonateComponent = () => {
  const { data: session, status: sessionStatus } = useSafeSession();

  const [isAuthenticated, setIsAuthenticated] = useState(sessionStatus === 'authenticated');
  const [isOpen, setIsOpen] = useState(false);
  const [authzSite, setAuthzSite] = useState<string[]>([]);
  const [authzSiteOptions, setAuthzSiteOptions] = useState<[string, string][]>([]);
  const [authzCenter, setAuthzCenter] = useState<string[]>([]);
  const [authzRegion, setAuthzRegion] = useState<ParsedRegionClaim>(parseRegionClaim([]));
  const [newValue, setNewValue] = useState('');
  const [impersonationAccount, setImpersonationAccount] = useState('');
  const router = useRouter();
  // Pull out view all roles into a parsed map
  const adminMap = extractViewAll(authzCenter);

  if (sessionStatus === 'authenticated' && session.view_all === true && !isAuthenticated) {
    // This is the first time we've rendered since the session was available.  Process user access
    const user = session?.user ?? {};
    const regionAuth = parseRegionClaim(
      'authz_region' in user ? (user.authz_region as Array<string>) || [] : []
    );
    setAuthzSite(session?.authz_site ?? []);
    setAuthzCenter(session?.center_types ?? []);
    if (!regionAuth.equals(authzRegion)) {
      setAuthzRegion(regionAuth);
    }
    setIsAuthenticated(true);
  }

  /**
   * A callback function to handle changes in the region checkboxes.
   */
  const onRegionChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const theField = e.target.dataset['field'] ?? '';
      if (theField in authzRegion) {
        setAuthzRegion({ ...authzRegion, [theField]: e.target.checked });
      }
    },
    [authzRegion]
  );

  /**
   * A callback function to process and load token response data.
   *
   * @param token - The JWT token containing claims to extract authorization details.
   *
   * The function extracts `authz_center`, `authz_region`, and `authz_site` claims
   * from the token, updates the corresponding state variables, and parses the
   * region authorization claim. If the parsed region authorization differs from
   * the current `authzRegion`, it updates the `authzRegion` state.
   *
   * Dependencies:
   * - `setAuthzSite`: Updates the authorization site state.
   * - `setAuthzCenter`: Updates the authorization center state.
   * - `setAuthzRegion`: Updates the authorization region state.
   * - `authzRegion`: The current authorization region state for comparison.
   */
  const loadTokenResponse = useCallback(
    (token: JWT) => {
      const {
        authz_center: thisAuthzCenter,
        authz_region: thisAuthzRegion,
        authz_site: thisAuthzSite,
      } = (token.claims ?? {}) as Record<string, string[]>;
      setAuthzSite(thisAuthzSite ?? []);
      setAuthzCenter(thisAuthzCenter ?? []);
      const regionAuth = parseRegionClaim(thisAuthzRegion);
      if (!regionAuth.equals(authzRegion)) {
        setAuthzRegion(regionAuth);
      }
    },
    [setAuthzSite, setAuthzCenter, setAuthzRegion, authzRegion]
  );

  /**
   * Centralized error handling utility function.
   * Logs the error and optionally displays an alert message.
   *
   * @param context - A string describing the context of the error.
   * @param error - The error object to log.
   */
  const handleApiError = (context: string, error: unknown) => {
    const err = makeLoggableError({ error });
    if (err.message === 'signal is aborted without reason') {
      logInfo(context, `Signal aborted (component unmount): ${err.message}`, { error });
    } else {
      logError(context, makeLoggableError({ error }));
    }
    return {};
  };

  /**
   * Fetches authorization site options and user claims when the user is authenticated.
   * Logs errors if the fetch fails.
   *
   * Dependencies:
   * - `isAuthenticated`: Indicates if the user is authenticated.
   * - `authzRegion`: The current authorization region state.
   * - `setAuthzSite`: Updates the authorization site state.
   * - `setAuthzCenter`: Updates the authorization center state.
   * - `loadTokenResponse`: Callback function to process the token response.
   *
   * @returns {Function} A cleanup function to abort the fetch request if necessary.
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return fnNoOp;
    }
    const controller = new AbortController();
    fetch(`${serviceUrl}?claim=authz_site`, { signal: controller.signal, method: 'OPTIONS' })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(setAuthzSiteOptions)
      .catch((error) => handleApiError('user-options', error));
    fetch(serviceUrl, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(loadTokenResponse)
      .catch((error) => handleApiError('user-api', error));
    return () => {
      controller?.abort();
    };
  }, [isAuthenticated, authzRegion, setAuthzSite, setAuthzCenter, loadTokenResponse]);

  useKonamiCode(() => setIsOpen(true), authzSite?.length > 0);

  /**
   * This function is used as a callback for the modal's close request.
   * @returns {void} Closes the impersonation modal by setting `isOpen` to false.
   */
  const closeModal = () => setIsOpen(false);

  /**
   * Adds a new value to the specified state array and resets the input field.
   * @param setter - The state setter function to update the array.
   * @param values - The current array of values.
   * @param newValue - The new value to be added to the array.
   * @returns {void} Updates the state with the new value and resets the input field.
   */
  const addValue = useCallback(
    (setter: Dispatch<SetStateAction<Array<string>>>, values: Array<string>) => {
      setter([...values, newValue]);
      setNewValue('');
    },
    [newValue]
  );

  /**
   * Handles the click event for loading the impersonation account.
   * Validates the user name and fetches claims for the specified account.
   */
  const onLoadAccountClicked = useCallback(async () => {
    const userName = (
      impersonationAccount.indexOf('@') === -1
        ? `${impersonationAccount ?? ''}@nmdp.org`
        : impersonationAccount
    ).trim();
    if (userName === '@nmdp.org') {
      // eslint-disable-next-line no-alert
      alert('User name is required');
      return;
    }
    try {
      await fetch('/api/auth/claims', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user: impersonationAccount }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(loadTokenResponse);
    } catch (error) {
      handleApiError('user-api', error);
    }
  }, [impersonationAccount, loadTokenResponse]);

  /**
   * Handles changes to the impersonation user input field.
   * @param e - The change event from the input field.
   * @returns {void} Updates the impersonation account state with the new value.
   */
  const onImpersonationUserValueChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setImpersonationAccount(e.target.value);
    },
    [setImpersonationAccount]
  );

  /**
   * Handles the click event for applying roles and updating the impersonation settings.
   * Validates the user name and sends a PUT request to update the roles.
   * Reloads the page upon successful update.
   */
  const onApplyRolesClicked = useCallback(() => {
    const body = {
      email: 'impersonated-user@nmdp.org',
      claims: {
        authz_center: authzCenter,
        authz_region: authzRegion,
        authz_site: authzSite,
      },
    };
    fetch(serviceUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(() => {
        if (router && router.isReady && 'reload' in router) {
          router.reload();
        }
      })
      .catch((error) => {
        handleApiError('user-api', error);
      });
  }, [authzCenter, authzRegion, authzSite, router]);

  /**
   * Checks if the impersonation button should be shown based on the length of authzSite.
   * @returns {boolean} True if the button should be shown, false otherwise.
   */
  const showButton = (authzSite?.length ?? 0) > 0;

  return (
    <>
      {showButton && (
        <Modal isOpen={isOpen} onRequestClose={closeModal} title="Impersonate User">
          <div className={classnames('mb-2')}>
            <h3 className={classnames('font-semibold')}>Sitecore Roles</h3>
            <ul>
              {React.Children.map(authzSite, (value: string, index: number) => (
                <li className={classnames('flex', 'items-center', 'justify-between')}>
                  {value}
                  <button type="button" onClick={() => removeValue(setAuthzSite, authzSite, index)}>
                    x
                  </button>
                </li>
              ))}
            </ul>
            <div className={classnames('flex', 'items-center', 'justify-between')}>
              <select
                aria-label="New Site Role"
                className={classnames('mt-2', 'flex-grow', 'rounded', 'border', 'p-2')}
              >
                {authzSiteOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <Button
                text="Add"
                onClick={() => addValue(setAuthzSite, authzSite)}
                className={classnames('bg-green', 'btn', 'ml-2', 'rounded', 'px-4', 'py-2')}
              />
            </div>
          </div>
          <div className={classnames('mb-4')}>
            <h3 className={classnames('font-semibold')}>Impersonate User</h3>
            <div className={classnames('flex', 'items-center', 'justify-between')}>
              TODO: Show a message re: impersonate status
            </div>
            <div className={classnames('flex', 'items-center', 'justify-between')}>
              <label
                htmlFor="txtImpersonateUser"
                className={classnames('mt-2', 'flex-grow', 'rounded', 'border', 'p-2')}
              >
                Impersonate Account:
                <input
                  id="txtImpersonateUser"
                  type="text"
                  aria-label="Impersonate Account"
                  value={impersonationAccount}
                  onChange={onImpersonationUserValueChanged}
                  className={classnames('mt-2', 'w-full', 'rounded', 'border', 'p-2')}
                />
              </label>
              <Button
                onClick={onLoadAccountClicked}
                className={classnames('btn-primary', 'btn', 'mt-2', 'rounded', 'px-4', 'py-2')}
                text="Load Account"
              />
            </div>
          </div>
          <div className={classnames('mb-4')}>
            <h3 className={classnames('font-semibold')}>Region</h3>
            {adminMap.ALL && (
              <div className={classnames('text-sm', 'text-warning')}>
                View All override in effect. Remove View All roles to configure Region individually.
              </div>
            )}
            <div className={classnames('mb-4', 'grid', 'grid-cols-2', 'gap-4')}>
              <label className={classnames('flex', 'items-center')} htmlFor="isUs">
                <input
                  type="checkbox"
                  className={classnames('mr-2')}
                  data-field="isUs"
                  onChange={onRegionChanged}
                  disabled={adminMap.ALL}
                  checked={adminMap.ALL || authzRegion.isUs}
                  id="isUs"
                />
                United States
              </label>
              <label className={classnames('flex', 'items-center')} htmlFor="isIntl">
                <input
                  type="checkbox"
                  className={classnames('mr-2')}
                  data-field="isIntl"
                  id="isIntl"
                  onChange={onRegionChanged}
                  disabled={adminMap.ALL}
                  checked={adminMap.ALL || authzRegion.isIntl}
                />
                International
              </label>
              <label className={classnames('flex', 'items-center')} htmlFor="isMexico">
                <input
                  type="checkbox"
                  className={classnames('mr-2')}
                  data-field="isMexico"
                  id="isMexico"
                  onChange={onRegionChanged}
                  disabled={adminMap.ALL}
                  checked={adminMap.ALL || authzRegion.isMexico}
                />
                Mexico
              </label>
            </div>
          </div>
          <div className={classnames('mb-4')}>
            <h3 className={classnames('font-semibold')}>Authz Center</h3>
            <div
              className={classnames(
                'grid',
                'grid-cols-1',
                'sm:grid-cols-2',
                'md:grid-cols-3',
                'lg:grid-cols-4',
                'xl:grid-cols-5',
                'gap-4',
                'overflow-y-auto',
                'max-h-64'
              )}
            >
              {React.Children.map(authzCenter, (value: string, index: number) => (
                <div
                  className={classnames(
                    'flex',
                    'items-center',
                    'justify-between',
                    'p-2',
                    'border',
                    'rounded'
                  )}
                >
                  {value}
                  <button
                    type="button"
                    onClick={() => removeValue(setAuthzCenter, authzCenter, index)}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
            <div className={classnames('flex', 'items-center', 'justify-between')}>
              <input
                type="text"
                aria-label="New Role"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className={classnames('mt-2', 'w-full', 'rounded', 'border', 'p-2')}
              />
              <Button
                onClick={() => addValue(setAuthzCenter, authzCenter)}
                className={classnames('bg-green', 'mt-2', 'rounded', 'px-4', 'py-2')}
                text="Add"
              />
            </div>
          </div>
          <div className={classnames('flex', 'items-center', 'justify-center')}>
            <Button onClick={closeModal} text="Cancel" className={classnames('mr-2')} />
            <Button onClick={onApplyRolesClicked} text="Apply" className={classnames('ml-2')} />
          </div>
        </Modal>
      )}
    </>
  );
};

/**
 * An empty shim component that serves as a placeholder for the `Impersonate` component.
 * This component is used to prevent the `Impersonate` component from being rendered
 * when the `env.SKIP_AUTHENTICATION` environment variable is set to `true`.
 * It is a no-op component that does not render anything to the DOM.
 * @returns {JSX.Element} An empty fragment.
 */
const ImpersonateShim = () => <></>;

/**
 * The `Impersonate` component is conditionally set to either the `ImpersonateComponent`
 * or the `ImpersonateShim` based on the value of the `env.SKIP_AUTHENTICATION` environment variable.
 * If `env.SKIP_AUTHENTICATION` is `true`, the `ImpersonateShim` is used to prevent rendering the component.
 * Otherwise, the `ImpersonateComponent` is used to provide the impersonation functionality.
 * This allows for easy toggling of the impersonation feature based on the environment configuration.
 */
const Impersonate = env.SKIP_AUTHENTICATION ? ImpersonateShim : ImpersonateComponent;

/**
 * The `Impersonate` component is the default export of this module.
 * It provides the impersonation functionality for the application.
 */
export default Impersonate;
